/**
 * LMDSFacturePublique — Page publique accessible par lien (sans auth)
 * Design identique à FacturePublique.jsx, adapté aux données Service Call LMDS
 */
import { useParams, useSearchParams } from 'react-router-dom';
import { ErreurPublique } from '../components/public/ErreurPublique';
import { LienRemplace } from '../components/public/LienRemplace';
import { usePublicFetch } from '../hooks/usePublicFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const MOIS_FR = ["","Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

function fmt(v) {
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(v || 0);
}
function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return d; }
}

export default function LMDSFacturePublique() {
  const { facture_id } = useParams();
  const [searchParams] = useSearchParams();
  const jeton = searchParams.get('t');  // 🔐 Sécurité v13.69 Q5 : jeton de lien public (sans expiration, révocable)
  const { data, loading, error } = usePublicFetch(
    facture_id ? `/api/lmds/factures/${facture_id}/view-public?t=${encodeURIComponent(jeton || '')}` : null,
    { notFoundMsg: 'Facture introuvable' }
  );

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #e5e7eb', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
        <p style={{ color: '#6b7280', fontSize: 14 }}>Chargement de la facture...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6' }}>
      {(typeof error === 'object' && error?.code === 'lien_remplace')
        ? <LienRemplace factureId={facture_id} message={error.message} typeApi={null} />
        : <ErreurPublique error={error} doc="Facture" onRetry={() => window.location.reload()} />}
    </div>
  );

  const facture    = data.facture    || {};
  const entreprise = data.entreprise || {};
  const client     = data.client     || {};
  const installer  = data.installer  || {};

  const lignes = facture.lignes || [];
  const moisLabel = MOIS_FR[facture.mois] || String(facture.mois || '');

  // Montants — gestion de l'ajustement
  const ajuste = facture.ajuste || false;
  const sousTotal  = ajuste ? (facture.montant_ajuste  ?? facture.sous_total ?? 0) : (facture.sous_total ?? 0);
  const tps        = ajuste ? round2(sousTotal * 0.05)  : (facture.tps  ?? 0);
  const tvq        = ajuste ? round2(sousTotal * 0.09975) : (facture.tvq ?? 0);
  const totalTTC   = ajuste ? (facture.total_ajuste_ttc ?? round2(sousTotal + tps + tvq)) : (facture.total_avec_taxes ?? round2(sousTotal + tps + tvq));

  const logoUrl = entreprise.logo_url || (entreprise.logo_principal ? `${API_URL}${entreprise.logo_principal}` : null);
  const partenairesUrls = entreprise.partenaires_urls || [];

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: letter; margin: 0.5in; }
          html, body { height: auto !important; overflow: visible !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; background: white !important; }
          #root { position: static !important; overflow: visible !important; height: auto !important; }
          .facture-wrapper { min-height: auto !important; background: white !important; padding: 0 !important; }
          .facture-card { box-shadow: none !important; border-radius: 0 !important; max-width: none !important; }
          div[style*="position: fixed"], div[style*="position:fixed"],
          [data-emergent-badge], #emergent-badge, .emergent-badge,
          a[href*="emergentagent"], a[href*="emergent"] { display: none !important; visibility: hidden !important; }
        }
      `}</style>

      <div className="facture-wrapper" style={{ minHeight: '100vh', background: '#f3f4f6', padding: '20px 12px' }}>

        {/* Bouton Imprimer */}
        <div className="no-print" style={{ maxWidth: 850, margin: '0 auto 12px', textAlign: 'right' }}>
          <button onClick={() => window.print()} style={{ padding: '8px 20px', background: '#2563eb', color: 'white', fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer' }}>
            Imprimer
          </button>
        </div>

        <div className="facture-card" style={{ maxWidth: 850, margin: '0 auto', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ padding: 32, fontSize: 12 }}>

            {/* HEADER — Logo + Entreprise + Numéros */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 20, alignItems: 'start' }}>
                <div>
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo" style={{ width: '100%', maxWidth: 280, height: 'auto', objectFit: 'contain' }} />
                  ) : (
                    <div style={{ width: '100%', height: 80, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6 }}>
                      <span style={{ color: '#9ca3af', fontSize: 11 }}>Logo</span>
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <h2 style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 8 }}>{entreprise.nom_entreprise}</h2>
                  <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                    <p>{entreprise.adresse}</p>
                    <p>{entreprise.ville} {entreprise.code_postal}</p>
                    <p style={{ marginTop: 6, fontWeight: 600 }}>{entreprise.telephone}</p>
                    <p>{entreprise.email}</p>
                    {entreprise.site_web && <p>{entreprise.site_web}</p>}
                  </div>
                </div>
                <div style={{ fontSize: 11 }}>
                  <p style={{ fontWeight: 'bold', fontSize: 12 }}>Facture: {facture.numero_facture}</p>
                  <p style={{ marginTop: 4 }}><strong>Date:</strong> {fmtDate(facture.date_facture)}</p>
                  <p style={{ marginTop: 4 }}><strong>Période:</strong> {moisLabel} {facture.annee}</p>
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb', fontSize: 10 }}>
                    {entreprise.numero_tps && <p><strong>TPS:</strong> {entreprise.numero_tps}</p>}
                    {entreprise.numero_tvq && <p><strong>TVQ:</strong> {entreprise.numero_tvq}</p>}
                    {entreprise.numero_rbq && <p><strong>RBQ:</strong> {entreprise.numero_rbq}</p>}
                    {entreprise.numero_neq && <p><strong>NEQ:</strong> {entreprise.numero_neq}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* TITRE */}
            <h1 style={{ fontSize: 26, fontWeight: 'bold', textAlign: 'center', margin: '20px 0' }}>
              FACTURE SERVICE CALL
            </h1>

            {/* AJUSTEMENT — badge si applicable */}
            {ajuste && (
              <div style={{ background: '#fefce8', border: '1px solid #fbbf24', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 12, textAlign: 'center' }}>
                <strong style={{ color: '#92400e' }}>Facture ajustée</strong>
                {facture.date_ajustement && <span style={{ color: '#78350f' }}> — {fmtDate(facture.date_ajustement)}</span>}
                {facture.raison_ajustement && <span style={{ color: '#78350f' }}> — {facture.raison_ajustement}</span>}
              </div>
            )}

            {/* CLIENT + INSTALLATEUR */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, fontSize: 12 }}>
                <div>
                  <p style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 6 }}>Facturé à :</p>
                  <p style={{ fontWeight: 600, marginBottom: 4 }}>{client.nom_entreprise || '—'}</p>
                  {client.adresse && <p>{client.adresse}</p>}
                  {client.ville && <p>{client.ville}{client.province ? `, ${client.province}` : ''} {client.code_postal || ''}</p>}
                  {client.telephone && <p>Téléphone: {client.telephone}</p>}
                  {client.email && <p>Courriel: {client.email}</p>}
                  {client.contact_nom && <p style={{ marginTop: 6 }}>Att: {client.contact_nom}{client.contact_poste ? ` — ${client.contact_poste}` : ''}</p>}
                </div>
                <div>
                  <p style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 6 }}>Installateur :</p>
                  <p style={{ fontWeight: 600 }}>{installer.prenom} {installer.nom}</p>
                  {facture.installer_nom && facture.installer_nom !== `${installer.prenom} ${installer.nom}` && (
                    <p>{facture.installer_nom}</p>
                  )}
                </div>
                <div>
                  <p style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 6 }}>Période :</p>
                  <p>{moisLabel} {facture.annee}</p>
                  {client.numero_tps && <p style={{ marginTop: 6, fontSize: 10 }}><strong>TPS client:</strong> {client.numero_tps}</p>}
                  {client.numero_tvq && <p style={{ fontSize: 10 }}><strong>TVQ client:</strong> {client.numero_tvq}</p>}
                </div>
              </div>
            </div>

            {/* TABLEAU DES LIGNES */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '10px 10px', textAlign: 'left', fontWeight: 600, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sem.</th>
                    <th style={{ padding: '10px 10px', textAlign: 'left', fontWeight: 600, fontSize: 9, textTransform: 'uppercase' }}>Client</th>
                    <th style={{ padding: '10px 10px', textAlign: 'left', fontWeight: 600, fontSize: 9, textTransform: 'uppercase' }}>Date</th>
                    <th style={{ padding: '10px 10px', textAlign: 'left', fontWeight: 600, fontSize: 9, textTransform: 'uppercase' }}>S.I.</th>
                    <th style={{ padding: '10px 10px', textAlign: 'left', fontWeight: 600, fontSize: 9, textTransform: 'uppercase' }}>Description</th>
                    <th style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 600, fontSize: 9, textTransform: 'uppercase' }}>SC</th>
                    <th style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 600, fontSize: 9, textTransform: 'uppercase' }}>Km</th>
                    <th style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 600, fontSize: 9, textTransform: 'uppercase' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l, i) => {
                    const sc  = parseFloat(l.service_call || 0);
                    const km  = parseFloat(l.km_montant  || 0);
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '8px 10px' }}>S.{l.semaine}</td>
                        <td style={{ padding: '8px 10px' }}>{l.nom_client || '—'}</td>
                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{l.date_rdv || '—'}</td>
                        <td style={{ padding: '8px 10px' }}>{l.si || '—'}</td>
                        <td style={{ padding: '8px 10px' }}>{l.description || '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{sc > 0 ? fmt(sc) : '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right' }}>{km > 0 ? fmt(km) : '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{fmt(sc + km)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* NOTES */}
            {facture.notes && (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Notes</h3>
                <p style={{ fontSize: 11, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{facture.notes}</p>
              </div>
            )}

            {/* TOTAUX */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
              <div style={{ width: 320 }}>
                {ajuste && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 12, color: '#6b7280', textDecoration: 'line-through' }}>
                      <span>Sous-total original</span>
                      <span>{fmt(facture.sous_total)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
                      <span>Sous-total ajusté</span>
                      <span>{fmt(sousTotal)}</span>
                    </div>
                  </>
                )}
                {!ajuste && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 12 }}>
                    <span>Sous-total</span>
                    <span>{fmt(sousTotal)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 12 }}>
                  <span>TPS (5%)</span>
                  <span>{fmt(tps)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 12 }}>
                  <span>TVQ (9,975%)</span>
                  <span>{fmt(tvq)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: 14, fontWeight: 'bold', borderTop: '2px solid #111' }}>
                  <span>Total (TTC)</span>
                  <span>{fmt(totalTTC)}</span>
                </div>
              </div>
            </div>

            {/* RÈGLEMENT */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Règlement de la facture</h3>
              <p style={{ fontWeight: 700, marginBottom: 6, fontSize: 12 }}>Virement Interac :</p>
              <p style={{ fontSize: 11, lineHeight: 1.7 }}>Pour acquitter cette facture, vous pouvez effectuer un virement Interac à l'adresse courriel de l'entreprise : <strong>{entreprise.email || 'info@peintureresidentielleqc.com'}</strong></p>
              <p style={{ fontSize: 11, marginTop: 4 }}>Aucun mot de passe ne sera requis.</p>
              <p style={{ fontWeight: 700, marginTop: 14, marginBottom: 6, fontSize: 12 }}>Chèque :</p>
              <p style={{ fontSize: 11, lineHeight: 1.7 }}>Vous pouvez acquitter cette facture par chèque en l'adressant à :</p>
              <p style={{ fontSize: 11, marginTop: 4, lineHeight: 1.8 }}>
                {entreprise.nom_entreprise || ''}<br />
                {entreprise.adresse || ''}<br />
                {entreprise.ville || ''} {entreprise.code_postal || ''}
              </p>
            </div>

            {/* TERMES */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Termes et conditions</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 11, lineHeight: 2 }}>
                <li style={{ padding: '3px 0', borderBottom: '1px dotted #e5e7eb' }}>Tous les prix ci-haut mentionnés sont en dollars Canadiens.</li>
                <li style={{ padding: '3px 0', borderBottom: '1px dotted #e5e7eb' }}>Paiement net 30 jours.</li>
                <li style={{ padding: '3px 0' }}>Frais d'administration de 2% par mois sur tout compte passé dû.</li>
              </ul>
            </div>

            {/* REMERCIEMENTS */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16, background: '#f0fdf4', textAlign: 'center' }}>
              <p style={{ fontStyle: 'italic', color: '#374151', fontWeight: 600, fontSize: 12 }}>
                Nous vous remercions de votre confiance et de votre collaboration !
              </p>
            </div>

            {/* PARTENAIRES */}
            {partenairesUrls.length > 0 && (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 16, padding: 16, textAlign: 'center' }}>
                <p style={{ fontSize: 15, fontWeight: 'bold', marginBottom: 10 }}>Nos partenaires</p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, flexWrap: 'wrap' }}>
                  {partenairesUrls.map((url, i) => (
                    <img key={i} src={url.startsWith('http') ? url : `${API_URL}${url}`} alt={`Partenaire ${i + 1}`} style={{ height: 36, width: 'auto', objectFit: 'contain' }} />
                  ))}
                </div>
              </div>
            )}

            {/* FOOTER */}
            <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
              {entreprise.nom_entreprise} — {entreprise.telephone} — {entreprise.email}
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

function round2(n) { return Math.round((n || 0) * 100) / 100; }
